'use client';

import {
  Alert,
  Copyright,
  IconSvg,
  ILocalAddress,
  PrismicImageWithAlt,
  Subscribe,
  useData,
} from '@/components';
import { useEcomStoreSelector } from '@/data';
import { useGapCommerceStore, useSelectedStore } from '@/hooks';
import {
  FooterSectionsSlice,
  FooterSectionsSliceDefaultItem,
  FooterSectionsSliceVariation,
  IPrismicSlice,
  ScheduleWorkingHours,
} from '@/types';
import {
  findMatchingDeliveryScheduleForAddress,
  formatEnUSPhoneNumber,
  getActiveWorkingHours,
  getReadableLabel,
  htmlSerializerWithProps,
} from '@/utils';
import * as prismicH from '@prismicio/helpers';
import { Dispatch, FC, ReactNode, SetStateAction, useMemo } from 'react';

import {
  ColorField,
  ImageFieldImage,
  isFilled,
  KeyTextField,
  RichTextField,
} from '@prismicio/client';
import { PrismicRichText } from '@prismicio/react';
import { asLink } from '@prismicio/helpers';
import classNames from 'classnames';
import Link from 'next/link';
import { DefaultVariation } from './DefaultVariation';
import { LinksAndSubscribeCenterVariation } from './LinksAndSubscribeCenterVariation';
import { SubscribeBottomVariation } from './SubscribeBottomVariation';
import { WithBackgroundImageVariation } from './WithBackgroundImageVariation';
import style from './footer.module.scss';
import {
  FooterLicenseRow,
  FooterLocationContactRow,
  resolveFooterLicenses,
  resolveFooterLocationContact,
} from './utils';

const SUBSCRIBE_BUTTON_LABEL = 'SUBSCRIBE';

const RenderFooter: FC<FooterSectionsSlice> = slice => {
  const sections: IFooterSection[] = useMemo(
    () => filterSections(slice?.items ?? []),
    [slice?.items],
  );

  if (slice.variation === 'subscribeBottom') {
    return <SubscribeBottomVariation sections={sections} slice={slice} />;
  }

  if (slice.variation === 'withBackgroundImage') {
    return <WithBackgroundImageVariation sections={sections} slice={slice} />;
  }

  if (slice.variation === 'linksAndSubscribeCenter') {
    return (
      <LinksAndSubscribeCenterVariation sections={sections} slice={slice} />
    );
  }

  return <DefaultVariation sections={sections} slice={slice} />;
};

export const FooterLogo = ({
  logo,
  variation,
}: {
  logo: ImageFieldImage | null | undefined;
  variation?: string;
}) => {
  const { footer } = useData();

  if (!logo) {
    return null;
  }

  return (
    <Link
      aria-label={'Go to home'}
      className={classNames({
        [style.logo]: variation === 'default',
        [style.subscribe_bottom_variation__logo]:
          variation === 'subscribeBottom',
        [style.with_background_image_variation__logo]:
          variation === 'withBackgroundImage',
        [style.links_and_subscribe_center_variation__footer__body__logo]:
          variation === 'linksAndSubscribeCenter',
      })}
      href={footer?.data?.logo_url || '/'}
    >
      <figure>
        <PrismicImageWithAlt
          defaultAlt="Brand logo"
          field={logo}
          height={logo?.dimensions?.height}
          quality={100}
          style={{
            objectFit: 'contain',
            width: '100%',
            height: 'auto',
          }}
          width={logo?.dimensions?.width}
        />
      </figure>
    </Link>
  );
};

export const FooterLink = ({ item, header_color, label_color }) => {
  if (item?.header && !item?.link) {
    return (
      <>
        <p
          className={classNames(style.section_title, 'footer_section_title')}
          style={{ color: header_color || '' }}
        >
          {item.label}
        </p>
      </>
    );
  }

  return (
    <>
      <Link
        aria-label={`Go to ${item?.label}`}
        className={classNames(style.section_link, 'footer_section_link')}
        href={item.link ?? ''}
        key={item.label}
        style={{ color: label_color }}
      >
        {item.header && (
          <p
            className={classNames(style.section_title, 'footer_section_title')}
            style={{ color: header_color }}
          >
            {item.label}
          </p>
        )}
        {!item.header && item.label}
      </Link>
    </>
  );
};

interface Error {
  isError: boolean;
  isOpen: boolean;
  message: string;
}

interface FooterSubscribeSectionProps {
  slice: FooterSectionsSliceVariation;
  error: Error;
  setError: Dispatch<SetStateAction<Error>>;
  onSubscribe: (_email: any) => void;
}

export const FooterSubscribeSection = ({
  slice,
  error,
  setError,
  onSubscribe,
}: FooterSubscribeSectionProps) => {
  const subscribe_header = slice?.primary?.subscribe_header;
  const subscribe_header_color = slice?.primary?.subscribe_header_color;
  const subscribe_input_placeholder =
    slice?.primary?.subscribe_input_placeholder;
  const subscribe_description_color =
    slice.variation === 'default' ||
    slice.variation === 'withBackgroundImage' ||
    slice.variation === 'linksAndSubscribeCenter'
      ? slice?.primary?.subscribe_description_color
      : '';
  const subscribe_description =
    slice.variation === 'default' ||
    slice.variation === 'withBackgroundImage' ||
    slice.variation === 'linksAndSubscribeCenter'
      ? slice?.primary?.subscribe_description
      : '';

  return (
    <div className={style.footer__subscribe}>
      {(subscribe_header || subscribe_description) && (
        <div className={style.footer__subscribe_header}>
          {subscribe_header ? (
            <p
              className={classNames(
                style.section_title,
                'footer_section_title',
                style.subscribe__header_title,
              )}
              style={{
                color: subscribe_header_color ?? '',
              }}
            >
              {subscribe_header}
            </p>
          ) : null}
          {subscribe_description ? (
            <p
              style={{
                color: subscribe_description_color ?? '',
              }}
            >
              {subscribe_description}
            </p>
          ) : null}
        </div>
      )}
      <div className={style.footer__form}>
        <Alert
          error={error.isError}
          isOpen={error.isOpen}
          toggle={() =>
            setError({
              isError: false,
              isOpen: false,
              message: '',
            })
          }
        >
          {error.message}
        </Alert>
        <Subscribe
          buttonIcon={slice.variation === 'default' ? 'arrow-right' : ''}
          className={style.container__subscribe}
          inFooter
          inputIcon={
            slice.variation === 'withBackgroundImage' ||
            slice.variation === 'linksAndSubscribeCenter'
          }
          isStandard={
            slice.variation !== 'withBackgroundImage' &&
            slice.variation !== 'linksAndSubscribeCenter'
          }
          label={
            slice.variation === 'subscribeBottom' ||
            slice.variation === 'withBackgroundImage' ||
            slice.variation === 'linksAndSubscribeCenter'
              ? SUBSCRIBE_BUTTON_LABEL
              : ''
          }
          onSubscribe={onSubscribe}
          placeholder={subscribe_input_placeholder ?? 'Enter your email'}
        />
      </div>
      {slice.variation === 'default' && <FooterSocialSection slice={slice} />}
    </div>
  );
};

export const FooterSocialSection = ({
  slice,
}: {
  slice: FooterSectionsSliceVariation;
}) => {
  return (
    <div
      className={classNames({
        [style.subscribe_bottom_variation__socials_container]:
          slice.variation === 'subscribeBottom',
        [style.with_background_image_variation__socials_container]:
          slice.variation === 'withBackgroundImage',
        [style.__socials_container]:
          slice.variation === 'linksAndSubscribeCenter',
        [style.socials_container]: slice.variation === 'default',
      })}
    >
      <div className={style.socials}>
        {Array.from({ length: 5 }).map((_, index) => {
          const { primary } = slice;
          const link = primary[`social_link_${index + 1}`];

          const icon = primary[`social_${index + 1}`];

          const netLink = asLink(link) || '/';

          if (!icon || Object.entries(icon).length === 0) return null;

          return (
            <Link
              aria-label={`Visit ${getReadableLabel(
                netLink,
              )} (opens in a new tab)`}
              className={style.socials__item}
              href={netLink}
              key={index}
              rel="noopener noreferrer"
              target="_blank"
            >
              <figure>
                <PrismicImageWithAlt
                  className={style.socials__icon}
                  defaultAlt="Social icon"
                  field={icon}
                  fill
                  sizes="24px"
                  style={{
                    objectFit: 'contain',
                  }}
                />
              </figure>
            </Link>
          );
        })}
      </div>
      {slice.variation === 'default' && <FooterCompanyBrand slice={slice} />}
    </div>
  );
};

export const FooterCompanyBrand = ({
  slice,
}: {
  slice: FooterSectionsSliceVariation;
}) => {
  const { footer_image_link, footer_image } = slice.primary;
  if (!footer_image?.url) {
    return null;
  }

  const href = prismicH.asLink(footer_image_link);
  const isExternal = footer_image_link?.link_type === 'Web';

  return (
    <Link
      aria-label={
        href ? `Go to ${getReadableLabel(href)}` : 'Go to partner site'
      }
      className={style.footer_image}
      href={href ?? '/'}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      target={isExternal ? '_blank' : undefined}
    >
      <figure>
        <PrismicImageWithAlt
          field={footer_image}
          fill
          sizes="72px"
          style={{
            objectFit: 'contain',
          }}
        />
      </figure>
    </Link>
  );
};

export const FooterInfoLegalSection = ({
  slice,
}: {
  slice: FooterSectionsSliceVariation;
}) => {
  const { warning_text, warning_text_color, company } = slice.primary;

  const term_and_use =
    slice.variation === 'default' ||
    slice.variation === 'withBackgroundImage' ||
    slice.variation === 'linksAndSubscribeCenter'
      ? slice?.primary?.term_and_use
      : '';
  const term_and_use_text_color =
    slice.variation === 'default' ||
    slice.variation === 'withBackgroundImage' ||
    slice.variation === 'linksAndSubscribeCenter'
      ? slice?.primary?.term_and_use_text_color
      : '';

  const isTerm =
    slice.variation === 'default' ||
    slice.variation === 'withBackgroundImage' ||
    slice.variation === 'linksAndSubscribeCenter'
      ? prismicH.asText(slice?.primary?.term_and_use)
      : undefined;
  const isWarning = prismicH.asText(slice.primary.warning_text);

  if (slice.variation == 'subscribeBottom') {
    return (
      <div className={style.subscribe_bottom_variation__info_legal}>
        {company && <span>{company}</span>}
        <div className={style.company_info}>
          <FooterLicense primary={slice.primary} />
          <FooterCompanyBrand slice={slice} />
        </div>

        {isTerm && (
          <PrismicRichText
            components={htmlSerializerWithProps({
              color: term_and_use_text_color,
            })}
            field={term_and_use as RichTextField}
          />
        )}
        {isWarning && (
          <PrismicRichText
            components={htmlSerializerWithProps({
              color: warning_text_color,
            })}
            field={warning_text}
          />
        )}
      </div>
    );
  }

  if (slice.variation == 'linksAndSubscribeCenter') {
    return (
      <div
        className={
          style.links_and_subscribe_center_variation__footer__body__info_legal
        }
      >
        <div className={style.logo_and_terms_container}>
          <FooterCompanyBrand slice={slice} />
          {isTerm && (
            <PrismicRichText
              components={htmlSerializerWithProps({
                color: term_and_use_text_color,
              })}
              field={term_and_use as RichTextField}
            />
          )}
        </div>
        {isWarning && (
          <PrismicRichText
            components={htmlSerializerWithProps({
              color: warning_text_color,
            })}
            field={warning_text}
          />
        )}
        <div className={style.company_info}>
          <FooterLicense primary={slice.primary} />
        </div>
        <FooterCopyright slice={slice} />
      </div>
    );
  }

  return (
    <div className={style.footer__info_legal}>
      {isTerm && (
        <PrismicRichText
          components={htmlSerializerWithProps({
            color: term_and_use_text_color,
          })}
          field={term_and_use as RichTextField}
        />
      )}
      {isWarning !== '' && (
        <PrismicRichText
          components={htmlSerializerWithProps({
            color: warning_text_color,
          })}
          field={warning_text}
        />
      )}
      <FooterLicense primary={slice.primary} />
      {slice.variation === 'default' && <FooterCopyright slice={slice} />}
    </div>
  );
};

export const FooterCopyright = ({
  slice,
}: {
  slice: FooterSectionsSliceVariation;
}) => {
  const { company, rights_text_color } = slice.primary;

  return (
    <div
      className={classNames({
        [style.footer__copyright_container]: slice.variation === 'default',
        [style.subscribe_bottom_variation__footer__copyright_container]:
          slice.variation === 'subscribeBottom',
        [style.with_background_image_variation__footer__copyright_container]:
          slice.variation === 'withBackgroundImage',
        [style.links_and_subscribe_center_variation__footer__copyright_container]:
          slice.variation === 'linksAndSubscribeCenter',
      })}
    >
      <Copyright
        business={company ?? ''}
        className={style.copyright}
        colorText={rights_text_color ?? ''}
        showCards={slice.variation !== 'linksAndSubscribeCenter'}
      />
    </div>
  );
};

export const filterSections = (
  items: FooterSectionsSliceDefaultItem[],
): IFooterSection[] => {
  const subgrupos: IFooterSection[] = [];
  let subgrupoActual: IFooterSection | null = null;

  for (const elemento of items) {
    if (elemento.header) {
      subgrupoActual = { header: elemento, links: [] };
      subgrupos.push(subgrupoActual);
    } else if (subgrupoActual) {
      subgrupoActual?.links?.push(elemento);
    } else {
      subgrupoActual = { header: undefined, links: [elemento] };
      subgrupos.push(subgrupoActual);
    }
  }

  return subgrupos;
};

export { RenderFooter };

export interface IFooterSection {
  header?: FooterSectionsSliceDefaultItem;
  links?: FooterSectionsSliceDefaultItem[];
}

// We don't need this logic becasue we have only one footer type
// supported today
export const getVariation = (body: IPrismicSlice[]): FooterSectionsSlice =>
  body?.find(
    slice => slice?.slice_type === 'footer_sections' && slice?.primary?.active,
  ) as FooterSectionsSlice;

export const FooterLicense = ({
  primary,
}: {
  // Index signature avoids weak-type errors when slice primaries in
  // prismicio-types lag behind FooterSections/model.json.
  primary: {
    location_contact_info?: FooterLicenseRow[] | null;
    license_text_color?: ColorField | null;
    [key: string]: unknown;
  };
}) => {
  const { treezStores } = useData();
  const { store: selectedStore } = useSelectedStore();

  const activeStore =
    selectedStore ??
    treezStores?.find(
      item =>
        item?.short_name === process.env.NEXT_PUBLIC_TREEZ_DEFAULT_STORE_NAME,
    ) ??
    treezStores?.[0];

  const licenses = resolveFooterLicenses(
    primary.location_contact_info,
    activeStore,
  );

  if (!licenses) {
    return null;
  }

  const textColor = { color: primary.license_text_color || undefined };

  if (licenses.source === 'prismic') {
    return (
      <div className={style.license_container} style={textColor}>
        <div className={style.license_rich_text}>
          <PrismicRichText
            components={htmlSerializerWithProps({
              color: primary.license_text_color,
            })}
            field={licenses.value}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={style.license_container}>
      <ul className={style.license_list}>
        {licenses.value.map((line, index) => (
          <li key={`${line}-${index}`} style={textColor}>
            <span>{line}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const locationSectionTitleClass = classNames(
  style.section_title,
  'footer_section_title',
);

const FooterLocationRichText = ({
  field,
  style: textStyle,
}: {
  field: RichTextField;
  style?: { color?: string };
}) => {
  const line = ({ children }: { children: ReactNode }) => (
    <p style={textStyle}>{children}</p>
  );

  return (
    <div className={style.rich_text}>
      <PrismicRichText
        components={{
          heading1: line,
          heading2: line,
          heading3: line,
          heading4: line,
          heading5: line,
          heading6: line,
          paragraph: line,
          list: ({ children }) => (
            <ul className={style.__address_section}>{children}</ul>
          ),
          oList: ({ children }) => (
            <ol className={style.__address_section}>{children}</ol>
          ),
          listItem: ({ children }) => <li style={textStyle}>{children}</li>,
          oListItem: ({ children }) => <li style={textStyle}>{children}</li>,
        }}
        field={field}
      />
    </div>
  );
};

const ContactLine = ({
  children,
  icon,
  label,
  labelColor,
}: {
  children: ReactNode;
  icon?: string;
  label?: string;
  labelColor?: ColorField;
}) => (
  <div className={style.address_item}>
    {icon ? <IconSvg name={icon} /> : null}
    {label ? (
      <span style={{ color: labelColor || undefined }}>{label}</span>
    ) : null}
    {children}
  </div>
);

export type FooterLocationPresentation = 'plain' | 'labels' | 'icons';

export const FooterLocationContact = ({
  primary,
  presentation = 'plain',
  title,
  headerColor,
  labelColor,
  compact = false,
  hoursBeside = false,
  showContactTitle = true,
}: {
  // Index signature avoids weak-type errors when slice primaries in
  // prismicio-types lag behind FooterSections/model.json.
  primary: {
    hours_section_title?: KeyTextField | null;
    location_contact_info?: FooterLocationContactRow[] | null;
    [key: string]: unknown;
  };
  presentation?: FooterLocationPresentation;
  title?: KeyTextField;
  headerColor?: ColorField;
  labelColor?: ColorField;
  compact?: boolean;
  hoursBeside?: boolean;
  showContactTitle?: boolean;
}) => {
  const { treezStores } = useData();
  const { store: selectedStore } = useSelectedStore();
  const { store: gcStore } = useGapCommerceStore();
  const { delivery_details, delivery_address } = useEcomStoreSelector([
    'delivery_details',
    'delivery_address',
  ]);
  const textColor = { color: labelColor || undefined };
  const titleColor = { color: headerColor || undefined };

  const activeStore =
    selectedStore ??
    treezStores?.find(
      item =>
        item?.short_name === process.env.NEXT_PUBLIC_TREEZ_DEFAULT_STORE_NAME,
    ) ??
    treezStores?.[0];

  const isPickup = !!delivery_details?.pickup;
  const storeShortName = activeStore?.short_name;
  const pickupHours = gcStore?.pickup_schedules?.find(
    schedule => schedule?.stores_provider_id === storeShortName,
  )?.working_hours;
  const deliveryHours =
    !isPickup && delivery_address && gcStore
      ? findMatchingDeliveryScheduleForAddress(
          delivery_address as ILocalAddress,
          gcStore,
          treezStores,
          storeShortName,
        )?.working_hours
      : undefined;
  const activeWorkingHours = getActiveWorkingHours({
    adminWorkingHours: (isPickup ? pickupHours : deliveryHours)?.filter(
      (day): day is ScheduleWorkingHours => !!day,
    ),
    configWorkingHours: activeStore?.working_hours,
  });

  const resolved = resolveFooterLocationContact(
    primary.location_contact_info,
    activeStore
      ? { ...activeStore, working_hours: activeWorkingHours }
      : activeStore,
  );

  const phoneText = resolved.phone?.value
    ? formatEnUSPhoneNumber(resolved.phone.value)
    : undefined;

  const hasContent = Boolean(
    resolved.address || phoneText || resolved.email || resolved.hours,
  );

  if (resolved.hidden || !hasContent) {
    return null;
  }

  const hasContactInfo = Boolean(
    resolved.address || phoneText || resolved.email,
  );

  return (
    <div
      className={classNames(style.location_contact, {
        [style.location_contact_compact]: compact,
        [style.location_contact_hours_beside]: hoursBeside,
      })}
    >
      <div className={style.contact_column}>
        {showContactTitle ? (
          <p className={locationSectionTitleClass} style={titleColor}>
            {isFilled.keyText(title) ? title : 'CONTACT INFO'}
          </p>
        ) : null}
        {hasContactInfo ? (
          <address className={style.__address_section}>
            {resolved.address && (
              <ContactLine
                icon={presentation === 'icons' ? 'pin' : undefined}
                label={presentation === 'labels' ? 'Address: ' : undefined}
                labelColor={labelColor}
              >
                <Link
                  aria-label={`View the address ${resolved.address} in a new tab`}
                  className="footer_section_link"
                  href={`https://www.google.com/search?q=${encodeURIComponent(
                    resolved.address,
                  )}`}
                  rel="noopener noreferrer"
                  style={textColor}
                  target="_blank"
                >
                  {resolved.address}
                </Link>
              </ContactLine>
            )}
            {resolved.email && (
              <ContactLine
                icon={presentation === 'icons' ? 'email' : undefined}
                label={presentation === 'labels' ? 'Email: ' : undefined}
                labelColor={labelColor}
              >
                <Link
                  aria-label={`Send email to ${resolved.email}`}
                  className="footer_section_link"
                  href={`mailto:${resolved.email}`}
                  style={textColor}
                >
                  {resolved.email}
                </Link>
              </ContactLine>
            )}
            {phoneText && (
              <ContactLine
                icon={presentation === 'icons' ? 'mobile' : undefined}
                label={presentation === 'labels' ? 'Phone: ' : undefined}
                labelColor={labelColor}
              >
                <Link
                  aria-label={`Call ${phoneText}`}
                  className="footer_section_link"
                  href={`tel:${phoneText}`}
                  style={textColor}
                >
                  {phoneText}
                </Link>
              </ContactLine>
            )}
          </address>
        ) : null}
      </div>
      {resolved.hours ? (
        <div className={style.hours_column}>
          <p
            className={classNames(locationSectionTitleClass, style.hours_title)}
            style={titleColor}
          >
            {isFilled.keyText(primary.hours_section_title)
              ? primary.hours_section_title
              : 'HOURS'}
          </p>
          {resolved.hours.source === 'prismic' ? (
            <FooterLocationRichText
              field={resolved.hours.value}
              style={textColor}
            />
          ) : (
            <ul
              className={classNames(style.__address_section, style.hours_list)}
            >
              {resolved.hours.value.map(line => (
                <li
                  className={style.hours_row}
                  key={`${line.day}-${line.period}`}
                >
                  <span className={style.hours_day} style={textColor}>
                    {`${line.day}:`}
                  </span>
                  <span className={style.hours_period} style={textColor}>
                    {line.period}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
};
